import { cn } from "@/lib/utils";

type AvatarStatus = "active" | "running" | "idle" | "paused" | "error" | string | null | undefined;

interface MobileAvatarProps {
  name: string;
  avatarUrl?: string | null;
  size?: number;
  status?: AvatarStatus;
  className?: string;
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

const STATUS_COLOR: Record<string, string> = {
  active: "bg-success",
  running: "bg-success",
  idle: "bg-text-muted",
  paused: "bg-warning",
  error: "bg-danger",
};

// Aurora avatar spec: 135° two-stop gradient per agent, initials white.
const GRADIENTS: [string, string][] = [
  ["#6D9BE8", "#3B77D8"],
  ["#34C79B", "#0FA37F"],
  ["#A78BFA", "#7C5CE0"],
  ["#F0A860", "#E07B39"],
  ["#5EC1D8", "#2E93B8"],
  ["#EE8CA5", "#D8567C"],
];

function gradientFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const [from, to] = GRADIENTS[h % GRADIENTS.length];
  return `linear-gradient(135deg, ${from}, ${to})`;
}

export function MobileAvatar({ name, avatarUrl, size = 36, status, className }: MobileAvatarProps) {
  const dotSize = Math.max(8, Math.round(size * 0.3));
  const fontSize = Math.round(size * 0.36);
  const radius = Math.round(size * 0.28);
  const dotColor = status ? STATUS_COLOR[status] : undefined;

  return (
    <div
      className={cn("relative shrink-0", className)}
      style={{ width: size, height: size }}
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className="h-full w-full object-cover"
          style={{ borderRadius: radius }}
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center font-semibold text-white"
          style={{
            borderRadius: radius,
            fontSize,
            letterSpacing: "-0.02em",
            background: gradientFor(name),
          }}
        >
          {getInitials(name)}
        </div>
      )}
      {dotColor && (
        <span
          className={cn("absolute rounded-full border-2 border-background", dotColor)}
          style={{ width: dotSize, height: dotSize, right: -2, bottom: -2 }}
        />
      )}
    </div>
  );
}
